import { useState } from 'react';
import { Button } from '@safetyhub/ui';
import Modal from '../Modal';
import { usersApi, extractApiError } from '../../lib/users';

/**
 * 푸터의 제출 버튼이 본문의 폼을 가리키는 `form` 속성 값. base Modal이 본문과 푸터를 형제
 * div로 렌더하므로 이 연결 없이는 `type="submit"`이 아무 폼에도 속하지 않는다.
 */
const FORM_ID = 'change-password-form';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function ChangePasswordModal({
  isOpen,
  onClose,
  onSuccess,
}: ChangePasswordModalProps) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // 비밀번호 유효성 검사 (8자 이상, 영문+숫자+특수문자)
  const isValidPassword = (value: string) => {
    return (
      value.length >= 8 &&
      /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])/.test(value)
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // 유효성 검사
    if (!currentPassword) {
      setError('현재 비밀번호를 입력해주세요.');
      return;
    }
    if (!newPassword) {
      setError('새 비밀번호를 입력해주세요.');
      return;
    }
    if (!isValidPassword(newPassword)) {
      setError('새 비밀번호는 8자 이상, 영문/숫자/특수문자를 포함해야 합니다.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('새 비밀번호가 일치하지 않습니다.');
      return;
    }
    if (currentPassword === newPassword) {
      setError('새 비밀번호는 현재 비밀번호와 달라야 합니다.');
      return;
    }

    setIsLoading(true);
    try {
      await usersApi.changePassword({ currentPassword, newPassword });
      onSuccess();
      handleClose();
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="비밀번호 변경"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
            취소
          </Button>
          {/* 푸터는 base가 본문과 **형제**로 렌더하므로 아래 `<form>` 밖이다 — `form` 속성으로
              이어야 제출이 일어난다(없으면 조용히 아무 일도 하지 않는다). */}
          <Button
            variant="primary"
            type="submit"
            form={FORM_ID}
            disabled={isLoading}
          >
            {isLoading ? '변경 중...' : '비밀번호 변경'}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={handleSubmit}>
        <div className="space-y-4">
          {/* 현재 비밀번호 */}
          <div>
            <label
              htmlFor="currentPassword"
              className="block text-sm font-medium text-ink-700"
            >
              현재 비밀번호
            </label>
            <input
              type="password"
              id="currentPassword"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
              placeholder="현재 비밀번호 입력"
              autoComplete="current-password"
            />
          </div>

          {/* 새 비밀번호 */}
          <div>
            <label
              htmlFor="newPassword"
              className="block text-sm font-medium text-ink-700"
            >
              새 비밀번호
            </label>
            <input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
              placeholder="새 비밀번호 입력"
              autoComplete="new-password"
            />
            <p className="mt-1 text-xs text-ink-500">
              8자 이상, 영문/숫자/특수문자 포함
            </p>
          </div>

          {/* 새 비밀번호 확인 */}
          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium text-ink-700"
            >
              새 비밀번호 확인
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
              placeholder="새 비밀번호 다시 입력"
              autoComplete="new-password"
            />
          </div>

          {/* 에러 메시지 */}
          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded-card">
              {error}
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
}
